/**
 * 匿名配置页面
 * 管理匿名用户功能配置
 */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Button,
  Form,
  Input,
  Switch,
  message,
  Space,
  Tag,
  Typography,
  List,
  Popconfirm,
  Empty,
  Avatar,
  Select,
} from 'antd'
import {
  SaveOutlined,
  ReloadOutlined,
  UserOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  ManOutlined,
  WomanOutlined,
  QuestionOutlined,
} from '@ant-design/icons'
import { anonymousApi } from '@/services/api'
import type { AnonymousConfig, AnonymousAvatar } from '@/types/system'

const { Title, Text } = Typography
const { Option } = Select

const AnonymousConfigPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState<AnonymousConfig | null>(null)
  const [form] = Form.useForm()

  // 获取匿名配置
  const fetchConfig = useCallback(async () => {
    setLoading(true)
    try {
      const response = await anonymousApi.getConfig()
      // 后端返回的是 {nicknames: [...]} 而不是 {nickname_prefixes: [...]}
      const rawData = response.data || {}
      const data = {
        enabled: rawData.enabled ?? false,
        nickname_prefixes: rawData.nicknames || rawData.nickname_prefixes || [],
        avatars: rawData.avatars || [],
        require_gender: rawData.require_gender ?? false,
      }
      setConfig(data)
      form.setFieldsValue({
        ...data,
        nickname_prefixes: data.nickname_prefixes?.join('\n') || '',
      })
    } catch (error: any) {
      message.error(`获取匿名配置失败: ${error.message || '未知错误'}`)
    } finally {
      setLoading(false)
    }
  }, [form])

  // 初始化
  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  // 保存配置
  const handleSave = async () => {
    try {
      const values = await form.validateFields()

      setSaving(true)

      // 后端期望 nicknames 而不是 nickname_prefixes
      const submitData = {
        enabled: values.enabled,
        nicknames: values.nickname_prefixes
          ?.split('\n')
          .map((s: string) => s.trim())
          .filter(Boolean) || [],
        avatars: config?.avatars || [],
        require_gender: values.require_gender,
      }

      await anonymousApi.updateConfig(submitData)
      message.success('保存匿名配置成功')
      fetchConfig()
    } catch (error: any) {
      if (error.errorFields) return
      message.error(`保存失败: ${error.message || '未知错误'}`)
    } finally {
      setSaving(false)
    }
  }

  // 添加头像
  const handleAddAvatar = () => {
    const url = prompt('请输入头像URL:')
    if (!url) return

    const gender = prompt('请选择性别 (male/female/neutral):', 'neutral') as 'male' | 'female' | 'neutral'
    if (!['male', 'female', 'neutral'].includes(gender)) {
      message.error('性别选择无效')
      return
    }

    const newAvatar: AnonymousAvatar = {
      url,
      gender,
    }

    setConfig(prev => prev ? {
      ...prev,
      avatars: [...prev.avatars, newAvatar],
    } : {
      enabled: false,
      nickname_prefixes: [],
      avatars: [newAvatar],
      require_gender: false,
    })

    message.success('添加头像成功，请保存配置')
  }

  // 删除头像
  const handleDeleteAvatar = (index: number) => {
    setConfig(prev => prev ? {
      ...prev,
      avatars: prev.avatars.filter((_, i) => i !== index),
    } : null)
    message.success('删除头像成功，请保存配置')
  }

  // 获取性别图标
  const getGenderIcon = (gender: string) => {
    switch (gender) {
      case 'male':
        return <ManOutlined className="text-blue-500" />
      case 'female':
        return <WomanOutlined className="text-pink-500" />
      default:
        return <QuestionOutlined className="text-gray-500" />
    }
  }

  // 获取性别标签
  const getGenderTag = (gender: string) => {
    switch (gender) {
      case 'male':
        return <Tag color="blue">男</Tag>
      case 'female':
        return <Tag color="pink">女</Tag>
      default:
        return <Tag>中性</Tag>
    }
  }

  return (
    <div className="p-6">
      {/* 页面标题和操作栏 */}
      <Card className="mb-4">
        <Space className="w-full justify-between" wrap>
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <EyeInvisibleOutlined className="text-purple-500" />
              匿名配置
            </h2>
            <p className="text-gray-500 text-sm">配置匿名用户功能和匿名选项</p>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchConfig}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              保存配置
            </Button>
          </Space>
        </Space>
      </Card>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          enabled: false,
          require_gender: false,
        }}
      >
        {/* 基本设置 */}
        <Card title="基本设置" className="mb-4">
          <Form.Item
            name="enabled"
            label="启用匿名功能"
            valuePropName="checked"
            extra="开启后用户可以选择匿名参与"
          >
            <Switch
              checkedChildren="启用"
              unCheckedChildren="禁用"
            />
          </Form.Item>

          <Form.Item
            name="require_gender"
            label="要求选择性别"
            valuePropName="checked"
            extra="开启后匿名用户必须选择性别"
          >
            <Switch
              checkedChildren="是"
              unCheckedChildren="否"
            />
          </Form.Item>
        </Card>

        {/* 昵称前缀配置 */}
        <Card
          title="昵称前缀"
          className="mb-4"
          extra={
            <Text type="secondary">
              每行一个前缀
            </Text>
          }
        >
          <Form.Item
            name="nickname_prefixes"
            extra="匿名用户的昵称前缀，系统将随机选择"
          >
            <Input.TextArea
              rows={6}
              placeholder={`神秘玩家\n匿名访客\n隐形人`}
            />
          </Form.Item>

          <div className="bg-gray-50 rounded p-3">
            <Text type="secondary" className="text-sm">
              示例昵称：神秘玩家_1234、匿名访客_5678
            </Text>
          </div>
        </Card>

        {/* 头像配置 */}
        <Card
          title="匿名头像"
          className="mb-4"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={handleAddAvatar}
            >
              添加头像
            </Button>
          }
        >
          {config?.avatars && config.avatars.length > 0 ? (
            <List
              grid={{ gutter: 16, xs: 2, sm: 3, md: 4, lg: 6 }}
              dataSource={config.avatars}
              renderItem={(avatar, index) => (
                <List.Item>
                  <Card
                    size="small"
                    cover={
                      <div className="p-4 text-center bg-gray-50">
                        <Avatar
                          size={64}
                          src={avatar.url}
                          icon={<UserOutlined />}
                        />
                      </div>
                    }
                    actions={[
                      <Popconfirm
                        title="确定删除此头像？"
                        onConfirm={() => handleDeleteAvatar(index)}
                        okText="删除"
                        cancelText="取消"
                      >
                        <DeleteOutlined className="text-red-500" />
                      </Popconfirm>,
                    ]}
                  >
                    <div className="text-center">
                      {getGenderTag(avatar.gender)}
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无头像配置"
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddAvatar}
              >
                添加头像
              </Button>
            </Empty>
          )}
        </Card>

        {/* 预览 */}
        <Card title="预览">
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded">
            <Avatar
              size={48}
              src={config?.avatars?.[0]?.url}
              icon={<UserOutlined />}
            />
            <div>
              <div className="font-medium">
                {config?.nickname_prefixes?.[0] || '匿名'}_****
              </div>
              <div className="text-sm text-gray-500">
                {config?.require_gender ? '已选择性别' : '未要求性别'}
              </div>
            </div>
            {config?.avatars?.[0]?.gender && getGenderIcon(config.avatars[0].gender)}
          </div>
        </Card>
      </Form>
    </div>
  )
}

export default AnonymousConfigPage
